import { Predicate } from '@lumapps/utils/types/Predicate';

import { Cell, isCell, isRow, Layout, Row, WidgetReference } from '../types';

type Node = Row | Cell | WidgetReference;

interface Options {
    /** Remove empty cells and rows */
    removeEmptyNodes?: boolean;
}

// Filter layout node recursively.
const filterNodeRecursively =
    <N extends Node>(predicate: Predicate<Node>, options: Options) =>
    (node: N): N | undefined => {
        if (!predicate(node)) {
            // Node invalid.
            return undefined;
        }
        if (isRow(node)) {
            // Filter cells.
            const cells = node.cells.map(filterNodeRecursively(predicate, options)).filter(Boolean);
            if (options.removeEmptyNodes && cells.length === 0) {
                return undefined;
            }
            return { ...node, cells };
        }
        if (isCell(node)) {
            // Filter children.
            const components = node.components.map(filterNodeRecursively(predicate, options)).filter(Boolean);
            if (options.removeEmptyNodes && components.length === 0) {
                return undefined;
            }
            return { ...node, components };
        }
        // No children to filter in widgets.
        return node;
    };

/**
 * Filter layout tree nodes to match a predicate.
 *
 * @example filterNodes(layout, (node) => isRow(node)) // => returns a layout with only rows (no cell or widget).
 *
 * @param layout    Layout tree.
 * @param predicate Predicate function used to filter the nodes.
 * @param options   Options object.
 */
export const filterLayoutNodes = (layout: Layout, predicate: Predicate<Node>, options: Options = {}): Layout => ({
    ...layout,
    // Filter children.
    components: layout.components.map(filterNodeRecursively(predicate, options)).filter(Boolean) as Row[],
});
